// Plan storage bridge — the single seam that lets a host shell redirect the
// plan to a native local store (e.g. SQLite in PCPDesk) WITHOUT touching the
// ~35 call-sites that read/write the plan straight from localStorage.
//
// The whole plan lives in localStorage under "pcp_plan_data" (+ side keys like
// "pcp_plan_data.corrupt"). Rather than refactor every controller, we install a
// TARGETED intercept on just that key on Storage.prototype. Everything else in
// localStorage (theme, sidebar order, tab lock, prefs, …) is untouched.
//
//   • In the browser (no backend set) this is a transparent passthrough to the
//     real localStorage — ZERO behavior change.
//   • A host (PCPDesk / WebView2) provides a backend implementing the SYNC
//     contract { getItem(k), setItem(k,v), removeItem(k) } — via
//     `window.__PCP_STORAGE__` set BEFORE the bundle boots (e.g. WebView2's
//     AddScriptToExecuteOnDocumentCreated), or later via setPlanBackend(). Only
//     the plan key (and its side keys) is routed to that backend, so the SAME
//     web editor persists to the local database instead of the browser.
//
// This module must load before any controller reads storage; bundle.js imports
// it first. setItem/removeItem on the backend may throw to signal failure,
// exactly like localStorage, so plan_storage's existing error handling still
// applies.

const PLAN_KEY = "pcp_plan_data"
const isPlanKey = (k) => k === PLAN_KEY || (typeof k === "string" && k.startsWith(PLAN_KEY + "."))

let _backend = null

// Validate + install a backend. Pass null/invalid to fall back to localStorage.
export function setPlanBackend(backend) {
  _backend = (backend &&
    typeof backend.getItem === "function" &&
    typeof backend.setItem === "function" &&
    typeof backend.removeItem === "function") ? backend : null
  return _backend != null
}
export function planBackend() { return _backend }
export function hasPlanBackend() { return _backend != null }

function install() {
  if (typeof window === "undefined" || !window.localStorage || !window.Storage) return
  if (window.__PCP_STORAGE_BRIDGE__) return   // idempotent (HMR / double-import)
  window.__PCP_STORAGE_BRIDGE__ = true

  // A host may inject its backend before the bundle runs.
  if (window.__PCP_STORAGE__) setPlanBackend(window.__PCP_STORAGE__)
  // …or hand one over afterwards without importing this module.
  window.__PCP_SET_STORAGE__ = setPlanBackend

  const proto = window.Storage.prototype
  const origGet = proto.getItem
  const origSet = proto.setItem
  const origRemove = proto.removeItem
  const ls = window.localStorage   // only intercept localStorage, never sessionStorage

  proto.getItem = function (k) {
    if (_backend && this === ls && isPlanKey(k)) {
      const v = _backend.getItem(k)
      return (v === undefined || v === null) ? null : String(v)
    }
    return origGet.call(this, k)
  }
  proto.setItem = function (k, v) {
    if (_backend && this === ls && isPlanKey(k)) { _backend.setItem(k, String(v)); return }
    return origSet.call(this, k, v)
  }
  proto.removeItem = function (k) {
    if (_backend && this === ls && isPlanKey(k)) { _backend.removeItem(k); return }
    return origRemove.call(this, k)
  }
}

install()
